import type { Editor } from '@tiptap/core'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextStyle } from '@tiptap/extension-text-style'
import { Typography } from '@tiptap/extension-typography'
import type { Content, UseEditorOptions } from '@tiptap/react'
import { useEditor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import * as React from 'react'

import { cn } from '../../../../lib/utils'
import {
    CodeBlockLowlight,
    Color,
    HorizontalRule,
    Image,
    Link,
    ResetMarksOnEnter,
    Selection,
    UnsetAllMarks,
} from '../extensions'
import { useThrottle } from '../hooks/use-throttle'
import { getOutput } from '../utils'

export interface UseMinimalTiptapEditorProps extends UseEditorOptions {
    value?: Content
    output?: 'html' | 'json' | 'text'
    placeholder?: string
    editorClassName?: string
    throttleDelay?: number
    onUpdate?: (content: Content) => void
    onBlur?: (content: Content) => void
}

const createExtensions = (placeholder: string) => [
    StarterKit.configure({
        horizontalRule: false,
        codeBlock: false,
        paragraph: { HTMLAttributes: { class: 'text-node' } },
        heading: { HTMLAttributes: { class: 'heading-node' } },
        blockquote: { HTMLAttributes: { class: 'block-node' } },
        bulletList: { HTMLAttributes: { class: 'list-node' } },
        orderedList: { HTMLAttributes: { class: 'list-node' } },
        code: { HTMLAttributes: { class: 'inline', spellcheck: 'false' } },
        dropcursor: { width: 2, class: 'ProseMirror-dropcursor border' },
    }),
    Link,
    Image.configure({ allowBase64: true }),
    Color,
    TextStyle,
    Selection,
    Typography,
    UnsetAllMarks,
    HorizontalRule,
    ResetMarksOnEnter,
    CodeBlockLowlight,
    Placeholder.configure({ placeholder: () => placeholder }),
]

export const useMinimalTiptapEditor = ({
    value,
    output = 'html',
    placeholder = '',
    editorClassName,
    throttleDelay = 1000,
    onUpdate,
    onBlur,
    ...props
}: UseMinimalTiptapEditorProps) => {
    const throttledSetValue = useThrottle((value: Content) => onUpdate?.(value), throttleDelay)

    const handleUpdate = React.useCallback(
        (editor: Editor) => throttledSetValue(getOutput(editor, output)),
        [output, throttledSetValue],
    )

    const handleCreate = React.useCallback(
        (editor: Editor) => {
            if (value && editor.isEmpty) {
                editor.commands.setContent(value)
            }
        },
        [value],
    )

    const handleBlur = React.useCallback(
        (editor: Editor) => onBlur?.(getOutput(editor, output)),
        [output, onBlur],
    )

    return useEditor({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        extensions: createExtensions(placeholder!),
        editorProps: {
            attributes: {
                autocomplete: 'off',
                autocorrect: 'off',
                autocapitalize: 'off',
                class: cn('focus:outline-hidden', editorClassName),
            },
        },
        onUpdate: ({ editor }) => handleUpdate(editor),
        onCreate: ({ editor }) => handleCreate(editor),
        onBlur: ({ editor }) => handleBlur(editor),
        ...props,
    })
}

export default useMinimalTiptapEditor
